import React, { useState } from 'react';
import { Modal } from '../../atoms/Modal';
import { Button } from '../../atoms/Button';
import { Input } from '../../atoms/Input';
import { Dropdown } from '../../atoms/Dropdown';
import { ErrorMessage } from '../../atoms/ErrorMessage';
import type { CreateInvitationInput, UserRole } from '@lnr-v2/types';
import styles from './InvitationModal.module.css';

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateInvitationInput) => Promise<void>;
}

/**
 * Modal for sending user invitations
 */
export const InvitationModal: React.FC<InvitationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roleOptions = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'editor', label: 'Editor' },
    { value: 'admin', label: 'Admin' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      await onSubmit({ email, role });

      // Reset form
      setEmail('');
      setRole('viewer');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setRole('viewer');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite User">
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formContent}>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="role" className={styles.label}>
              Role
            </label>
            <Dropdown
              id="role"
              value={role}
              onChange={(value) => setRole(value as UserRole)}
              options={roleOptions}
              disabled={loading}
            />
            <p className={styles.roleDescription}>
              {role === 'viewer' && 'Can view content but cannot make changes'}
              {role === 'editor' && 'Can create and edit content'}
              {role === 'admin' &&
                'Full access to manage users, content, and settings'}
            </p>
          </div>

          {error && <ErrorMessage message={error} />}
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            glass
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !email}
            loading={loading}
            glass
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
